import React from 'react';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import Card from '@common/components/card';
import Stack from '@common/components/stack';
import { Button } from '@common/components/button';
import { useTranslation } from 'react-i18next';

export type RatingScale = {
  id: string | number;
  label: string;
  description?: string;
  index: number;
  color: string;
};

type RatingScalesModalProps = {
  show: boolean;
  onHide: () => void;
  scales: RatingScale[];
  title?: string;
  showConfirmButton?: boolean;
  onConfirm?: () => void;
  confirmButtonText?: string;
  isLoading?: boolean;
};

const RatingScalesModal = ({
  show,
  onHide,
  scales,
  title,
  showConfirmButton = false,
  onConfirm,
  confirmButtonText,
  isLoading = false,
}: RatingScalesModalProps) => {
  const { t } = useTranslation();

  return (
    <ModalWindow
      show={show}
      onHide={onHide}
      size="small"
      className="EditableModule"
    >
      <ModalContent
        title={title || t('performance:rating_scales')}
        onHide={onHide}
        hideFooter
      >
        <div className="tw-flex tw-flex-col tw-gap-3">
          {scales?.map((scale) => (
            <Card
              key={scale.id}
              containerClassName="!tw-bg-gray-100 !tw-shadow-input"
            >
              <Card.Content className="tw-flex tw-flex-col !tw-p-5 !tw-gap-5">
                <Stack direction="row" gap={2} align="start">
                  <div
                    className="tw-text-base tw-font-medium tw-text-white
                    tw-rounded-lg tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center"
                    style={{ backgroundColor: scale.color }}
                  >
                    {scale.index}
                  </div>
                  <Stack direction="column" gap={1}>
                    <div className="tw-text-gray-textDark tw-text-base tw-font-medium">
                      {scale.label}
                    </div>
                    {scale.description && (
                      <div className="tw-text-gray-text tw-whitespace-pre-line tw-text-sm-plus tw-font-normal">
                        {scale.description}
                      </div>
                    )}
                  </Stack>
                </Stack>
              </Card.Content>
            </Card>
          ))}
        </div>
        {showConfirmButton && (
          <div className="tw-flex tw-justify-end tw-pt-4">
            <Button
              type="primary"
              size="large"
              onClick={onConfirm}
              isLoading={isLoading}
              disabled={isLoading}
            >
              {confirmButtonText}
            </Button>
          </div>
        )}
      </ModalContent>
    </ModalWindow>
  );
};

export default RatingScalesModal;
